/**
 * LES EXERCICES DU MANUEL VIVANT — registres, format d'échange et contrats des
 * routes (chantier « manuel vivant », Φ-MV-3 ; MV-EXO-01 à 10, MV-MAI-01 à 05,
 * MV-CNT-01 à 04, MV-PRG-06).
 *
 * Ce fichier est une COUTURE. Trois lecteurs s'y accordent :
 *
 *  - la chaîne de contenu, qui écrit `contenu/<matière>/<niveau>/<chapitre>/
 *    exercices/*.json` (§ 3) ;
 *  - le serveur, qui importe, sert et corrige (`modules/manuel/`) ;
 *  - l'application et la console, qui lisent les réponses des routes (§ 4, § 5).
 *
 * ─── Deux conventions à connaître avant de lire les contrats ────────────────
 *
 * UN « TEXTE RICHE » est de la prose où les mathématiques sont écrites en LaTeX
 * restreint ENTRE `\(` et `\)` (liste fermée : `LATEX_COMMANDES_PERMISES`). Tout
 * énoncé, indice, message et commentaire rendu par ces routes en est un.
 *
 * UNE « ÉCRITURE » est du LaTeX restreint SANS délimiteurs : `2^{7}`,
 * `3{,}4 \times 10^{7}`. C'est la forme d'une réponse saisie dans le champ
 * mathématique, d'une réponse attendue et d'une réponse fautive prévue.
 *
 * ─── Ce que l'élève ne reçoit JAMAIS avant d'avoir répondu ──────────────────
 *
 * Ni la réponse attendue, ni les écritures équivalentes, ni les réponses
 * fautives prévues, ni la démarche de référence : `GET /v1/manuel/exercices/
 * :code` ne les porte pas. Elles n'arrivent qu'avec la correction
 * (`CorrectionTentativeResponse`). Et aucun nombre de maîtrise n'arrive jamais :
 * un palier se dit en mots, un repère en comptes (AD-MV-7).
 */
import { z } from 'zod';
import { BOUTS_V2, FAMILLES_TRACEES_V2, PARTIES_MAX_V2, PARTIES_MIN_V2, nombreOuPiV2, repereV2VersDto, } from '../figures/figure-v2.js';
import { COLONNES_DE_MATRICE, COLONNES_DE_TABLE, LIGNES_DE_MATRICE, LIGNES_DE_TABLE, } from '../fiches/table-valeurs.js';
import { lireCouple, lireEnsemble } from '../math/ensembles.js';
import { lireEnsembleSolution } from '../math/ensembles-exacts.js';
import { diagrammeDecritSchema, diagrammeSchema } from './diagramme.schema.js';
import { aimantSchema, droiteALOeilSchema, experienceSchema, tacheDeGrapheSchema, } from './manipulation.schema.js';
import { solideDecritSchema, solideSchema } from './solide.schema.js';
import { tacheSolideSchema, vueSolideSchema } from './solide-manipule.schema.js';
import { consigneEcritureSchema } from './tache-ecriture.js';
/* -------------------------------------------------------------------------- */
/* 1. Les registres                                                           */
/* -------------------------------------------------------------------------- */
/**
 * Le nom d'un niveau d'exercice, et ce qu'on y fait (MV-PRG-06). Les noms sont
 * ceux du chantier « Élève » (lot 1, décision du fondateur du 17 octobre 2026) :
 * Facile, Intermédiaire, Avancé, Situation-problème. Seuls les LIBELLÉS ont
 * changé : les numéros 1 à 4 restent la clé du contenu et de la console.
 */
export const NIVEAUX_EXERCICE_NOMMES = {
    /* Des mots de TOUT chapitre : « une loi » était le vocabulaire des exposants. */
    1: { nom: 'Facile', description: 'Une idée, un pas.' },
    2: { nom: 'Intermédiaire', description: 'Deux ou trois étapes à la suite.' },
    3: { nom: 'Avancé', description: 'Justifier, trouver ce qui cloche, comparer.' },
    4: {
        nom: 'Situation-problème',
        description: 'Un contexte, plusieurs notions, une démarche complète à organiser.',
    },
};
/** `valeur` est-elle un niveau d'exercice (1 à 4) ? */
export function estNiveauExercice(valeur) {
    return valeur === 1 || valeur === 2 || valeur === 3 || valeur === 4;
}
/**
 * Le niveau d'exercice n'est PAS une quatrième échelle de difficulté :
 * `ContentItem.exigence` en est dérivée par cette table, pour les lecteurs qui
 * ne connaissent que les trois crans (carte 02, § 7.3).
 */
export function exigenceDuNiveauExercice(niveau) {
    if (niveau === 1)
        return 'accessible';
    if (niveau === 2)
        return 'intermediaire';
    return 'exigeant';
}
/**
 * La table INVERSE (MV-MAI-12) : le niveau d'exercice sous lequel un item qui
 * ne connaît que les trois crans — un défi du jour — entre au journal par
 * notion. Un défi n'est jamais une situation-problème : il ne rend pas 4.
 */
export function niveauExerciceDeLExigence(exigence) {
    if (exigence === 'accessible')
        return 1;
    if (exigence === 'intermediaire')
        return 2;
    return 3;
}
/**
 * Le nom et la description d'un niveau, DANS UNE MATIÈRE (décision D-8 du
 * 8 octobre 2026). Les mathématiques gardent `NIVEAUX_EXERCICE_NOMMES` mot pour
 * mot ; le français ne parle ni de « lois » ni de « calculer ». Son niveau 4
 * (des tâches d'écriture, souvent) s'appelle « Défi » : il n'y a pas de
 * situation-problème en français (fondateur, 19 oct. 2026, AB#961).
 */
const NIVEAUX_EXERCICE_FRANCAIS = {
    1: { nom: 'Facile', description: 'Une idée, un pas.' },
    2: { nom: 'Intermédiaire', description: 'Deux ou trois étapes à la suite.' },
    3: { nom: 'Avancé', description: 'Lire, relever, justifier un choix d’écriture.' },
    4: {
        nom: 'Défi',
        description: 'Un texte à lire, un texte à écrire, plusieurs notions à tenir ensemble.',
    },
};
export function niveauExerciceNomme(niveau, matiere) {
    return (matiere === 'francais' ? NIVEAUX_EXERCICE_FRANCAIS : NIVEAUX_EXERCICE_NOMMES)[niveau];
}
/**
 * Les compétences qu'un exercice du manuel déclare — codes de `CompetencePFEQ`.
 * Mathématiques : `MAT-C2` aux niveaux 1 à 3, `MAT-C1` au niveau 4. Français :
 * `FR-C1` (lire) ou `FR-C2` (écrire), à tout niveau.
 */
export const COMPETENCES_EXERCICE = ['MAT-C1', 'MAT-C2', 'FR-C1', 'FR-C2'];
/** `valeur` est-elle une compétence d'exercice ? Les lecteurs de la base rendent `null` sinon. */
export function estCompetenceExercice(valeur) {
    return COMPETENCES_EXERCICE.includes(valeur);
}
/** Les compétences qu'une matière admet. */
export function competencesDeLaMatiere(matiere) {
    return COMPETENCES_EXERCICE.filter((c) => c.startsWith(matiere === 'francais' ? 'FR-' : 'MAT-'));
}
/** Les catégories de la banque, et leur libellé de filtre (MV-EXO-10). */
export const CATEGORIES_EXERCICE = [
    'calcul',
    'simplifier',
    'conversion',
    'reconnaitre',
    'vrai-faux',
    'trouver-l-erreur',
    'comparer',
    'justifier',
    'estimer',
    /** Répondre par un tracé dans l'atelier graphique (nature `trace`, Φ-MV-8). */
    'tracer',
    'situation-probleme',
];
export const LIBELLES_CATEGORIE_EXERCICE = {
    calcul: 'Calcul',
    simplifier: 'Simplifier',
    conversion: 'Convertir',
    reconnaitre: 'Reconnaître',
    'vrai-faux': 'Vrai ou faux',
    'trouver-l-erreur': 'Trouver l’erreur',
    comparer: 'Comparer',
    justifier: 'Justifier',
    estimer: 'Estimer',
    tracer: 'Tracer',
    'situation-probleme': 'Situation-problème',
};
/**
 * Les NATURES de réponse (MV-EXO-02). Elles ne sont pas des valeurs de
 * `FormeReponse` : la nature fine vit dans `ContentItem.corrige`, et la forme
 * du produit s'en déduit (`FORME_DE_LA_NATURE`).
 *
 * La sixième, `trace` (Φ-MV-8, analyse 05, L-2 et L-3) : l'élève répond par une
 * CONSTRUCTION dans l'atelier graphique, et le code la vérifie
 * (`verifierConstruction`) — ni modèle, ni crédits.
 *
 * La septième, `texte` (décisions D-3, D-5 et D-6 du 8 octobre 2026) : l'élève
 * répond EN PROSE — une phrase, un paragraphe, un texte. Aucun calcul n'a
 * d'avis sur elle : le modèle la lit critère par critère, le CODE en tire le
 * verdict, et sans lecture elle reste « en attente de lecture » — jamais un
 * essai compté.
 */
export const NATURES_REPONSE_EXERCICE = [
    'nombre',
    'expression',
    'choix',
    'vrai_faux',
    'demarche',
    'trace',
    'texte',
    /* `CAP-10` (tranche T6) — trois réponses FERMÉES de plus, corrigées sans
       modèle : le couple (solution d'un système, coordonnées d'un point),
       l'intervalle, et l'ensemble-solution d'une inéquation à une variable. */
    'couple',
    'intervalle',
    'ensemble_solution',
    /* `CAP-09` (tranche T7) — une TABLE DE VALEURS à remplir, corrigée case par case. */
    'table',
    /* `CAP-08` (tranche T8) — un DIAGRAMME STATISTIQUE à construire, corrigé élément par élément. */
    'diagramme',
    /* Tranche T41 — une MATRICE à remplir en entier (dimensions données), corrigée
       case par case, par le calcul. Elle emprunte la mécanique de la table. */
    'matrice',
    /* Tranche T43 (P-VECTEUR) — un VECTEUR par ses composantes : la lecture et la
       correction du couple, mais l'écran écrit son nom et ses parenthèses, et
       l'historique le montre comme un vecteur, jamais comme un point. */
    'vecteur',
    /* Atelier de figures, palier L6 (F10) — UN SOLIDE qu'on tourne : désigner un
       élément (une arête, une face, la hauteur) ou compléter un patron. Le CODE
       juge l'identité de l'élément, les faces voisines, le pliage sans recouvrement. */
    'solide',
];
export const FORME_DE_LA_NATURE = {
    nombre: 'reponse_courte',
    expression: 'reponse_courte',
    choix: 'choix_multiple',
    vrai_faux: 'choix_multiple',
    demarche: 'demarche',
    trace: 'construction',
    texte: 'redaction',
    couple: 'reponse_courte',
    vecteur: 'reponse_courte',
    intervalle: 'reponse_courte',
    ensemble_solution: 'reponse_courte',
    table: 'reponse_courte',
    diagramme: 'reponse_courte',
    matrice: 'reponse_courte',
    solide: 'construction',
};
/** Les deux options d'un vrai ou faux : leurs identifiants sont la réponse à envoyer. */
export const OPTIONS_VRAI_FAUX = [
    { id: 'vrai', texte: 'Vrai' },
    { id: 'faux', texte: 'Faux' },
];
/**
 * La zone de démarche d'un exercice (MV-EXO-02) : `attendue` (ouverte d'office :
 * niveau 3 ou 4, nature `demarche`, vrai ou faux), `facultative` (derrière
 * « Écrire ma démarche »), `aucune` (jamais de démarche pour un choix).
 */
export const PLACES_DEMARCHE = ['attendue', 'facultative', 'aucune'];
export function placeDeLaDemarche(nature, niveau) {
    /* Ni un choix ni un tracé ne se justifient par des lignes : le tracé EST la démarche. */
    /* Ni une réponse rédigée : le texte EST la réponse, il n'y a rien à écrire à côté. */
    if (nature === 'choix' || nature === 'trace' || nature === 'texte' || nature === 'solide') {
        return 'aucune';
    }
    if (nature === 'demarche' || nature === 'vrai_faux' || niveau >= 3)
        return 'attendue';
    return 'facultative';
}
/** Les trois critères d'une situation-problème, et ce que l'élève en lit (MV-EXO-03). */
export const CRITERES_SITUATION_PROBLEME = [
    'comprehension',
    'mobilisation',
    'communication',
];
export const LIBELLES_CRITERE_SITUATION = {
    comprehension: 'Comprendre la situation',
    mobilisation: 'Utiliser les bonnes lois',
    communication: 'Montrer ta démarche',
};
/**
 * D'OÙ VIENT l'exercice qu'un élève fait — `TentativeExercice.origine`
 * (MV-EXO-04). À ne pas confondre avec la SOURCE du journal, qui s'en déduit.
 */
export const ORIGINES_TENTATIVE = [
    /** Choisi dans la banque, ou proposé par le moteur. */
    'entrainement',
    /** Déposé par le tuteur dans une séquence. */
    'sequence_tuteur',
    /** Proposé après une copie de devoir. */
    'aide_aux_devoirs',
    /** Proposé par la carte « À toi de jouer » d'une capsule. */
    'capsule',
    /** Ouvert depuis une étape d'un parcours (lot P1, AB#973). Ajouté À LA FIN. */
    'parcours',
];
/** La source du journal par notion que chaque origine écrit — une valeur du registre. */
export const SOURCE_DE_L_ORIGINE = {
    entrainement: 'exercice_manuel',
    sequence_tuteur: 'sequence_tuteur',
    aide_aux_devoirs: 'exercice_manuel',
    capsule: 'capsule',
    /* Un parcours est le plus souvent écrit par l'élève ou l'ébauche : de
       l'entraînement. Celui d'un tuteur ouvert depuis sa liste garde `sequence_tuteur`. */
    parcours: 'exercice_manuel',
};
/** Indices d'un exercice : exactement deux, demandés un à un. */
export const NOMBRE_INDICES_EXERCICE = 2;
/** Bornes de ce qu'un élève envoie à la correction. */
export const MAX_LIGNES_DEMARCHE = 30;
export const MAX_CARACTERES_LIGNE = 500;
export const MAX_CARACTERES_REPONSE = 500;
/** Une réponse RÉDIGÉE (nature `texte`) : un texte de 160 mots tient en 1 100 caractères. */
export const MAX_CARACTERES_TEXTE = 4000;
/* ── Répondre AU STYLET (demande du fondateur du 9 octobre 2026) ─────────── */
/**
 * Les pages d'une réponse manuscrite : autant que l'encre d'un défi en garde
 * (`MAX_PAGES_ENCRE`). Une démarche plus longue se répond au clavier.
 */
export const MAX_PAGES_MANUSCRITES = 4;
/** Une page du canevas, en base64 : une image PNG d'au plus 1 400 px de large. */
export const MAX_OCTETS_BASE64_PAGE = 4_000_000;
/** Les natures de réponse qui s'écrivent au stylet : une démarche, un texte. */
export const NATURES_MANUSCRITES = ['demarche', 'texte'];
/** 422 : rien ne se lit sur les pages. Ni essai, ni maîtrise ; les pages restent à l'écran. */
export const CODE_MANUSCRIT_ILLISIBLE = 'MANUSCRIT_ILLISIBLE';
/** 503 : la lecture n'a pas abouti (délai, refus, sortie hors contrat). */
export const CODE_LECTURE_MANUSCRITE_INDISPONIBLE = 'LECTURE_ENCRE_INDISPONIBLE';
/** Sous cette confiance, une ligne lue est signalée « à vérifier ». */
export const CONFIANCE_LIGNE_MANUSCRITE = 0.7;
/** Bornes des traits d'une page : au-delà, la page se lit par son image. */
export const MAX_TRAITS_PAGE = 3000;
export const MAX_POINTS_TRAIT = 2000;
export const MAX_POINTS_PAGE = 60_000;
/** Une coordonnée de trait : dans l'image, avec la marge d'un trait qui déborde. */
export const MAX_COORDONNEE_TRAIT = 8000;
/** Les mots d'une ligne lue, tels que `motsAVerifier` les compte. */
export const motsDeLaLigne = (texte) => texte.trim() === '' ? [] : texte.trim().split(/\s+/);
/**
 * Qui a lu les pages. `traits` : le lecteur d'encre, sur les traits ;
 * `image` : le même lecteur, sur l'image (traits absents ou refusés) ;
 * `modele` : le modèle « qualité », repli d'une démarche quand le lecteur
 * d'encre est indisponible ; `prose` : le modèle courant sous la consigne de
 * transcription stricte — une réponse rédigée (banc du 9 octobre 2026).
 */
export const LECTEURS_MANUSCRIT = ['traits', 'image', 'modele', 'prose'];
/**
 * Ce qu'il en est du TÉMOIN d'une réponse au stylet. `compare` : un second
 * lecteur a relu les mêmes pages, et ce où il diverge est désigné — des mots
 * (`motsAVerifier`, réponse rédigée) ou des lignes (`aVerifier`, démarche) ;
 * `indisponible` : il n'a pas pu lire — rien n'est désigné, l'élève relit tout.
 */
export const ETATS_TEMOIN_LECTURE = ['compare', 'indisponible'];
/**
 * LES TÉMOINS qu'une réponse au stylet peut avoir. Réponse rédigée (réglage
 * `TEMOIN_LECTURE_PROSE` de l'API) — `azure-read` : Azure AI Vision « Read »,
 * sur l'IMAGE de la page, hébergé au Canada ; `mathpix-traits` : Mathpix, sur
 * les TRAITS. Démarche (réglage `TEMOIN_LECTURE_DEMARCHE`) — `modele-courant` :
 * le modèle courant, sur l'IMAGE, sous la consigne de transcription stricte
 * (requête P7D ; banc du stylet, variante C2).
 */
export const TEMOINS_LECTURE = ['azure-read', 'mathpix-traits', 'modele-courant'];
/** Bornes de `LectureRetenueDto.lignes` : celles d'une réponse rédigée de quatre pages. */
export const MAX_LIGNES_LECTURE_RETENUE = 200;
export const MAX_CARACTERES_LIGNE_LUE = 2000;
/* ── Les paliers d'une notion ────────────────────────────────────────────── */
/** Les quatre paliers que les écrans du manuel disent, dans l'ordre. */
export const PALIERS_NOTION = ['nouveau', 'en_progression', 'a_consolider', 'acquis'];
export const LIBELLES_PALIER_NOTION = {
    nouveau: 'Nouveau',
    en_progression: 'En progression',
    a_consolider: 'À consolider',
    acquis: 'Acquis',
};
/**
 * Ce que dit `libellePalier` d'une notion SANS AUCUN TRAVAIL rangé sous elle
 * (`UX-7-01`, Φ-MV-7). Son `palier` reste `nouveau` — l'énumération ne change
 * pas —, son `travaillee` est `false`, et aucune tendance ne lui est prêtée.
 * « Nouveau » se lit donc seulement à partir du premier travail.
 */
export const LIBELLE_NOTION_NON_TRAVAILLEE = 'Pas encore travaillée';
/**
 * La projection des quatre paliers sur `NiveauMaitrise`, sans lui ajouter de
 * valeur (`DECISIONS-ARCHITECTE.md`, point 3).
 *
 * ⚠️ La décision nomme les cibles « non_evalue, en_construction, en_voie,
 * acquis » ; l'énumération porte `non_evalue`, `en_decouverte`,
 * `en_consolidation`, `en_voie_de_maitrise`, `maitrise`. « en_voie » et
 * « acquis » se lisent sans doute (`en_voie_de_maitrise`, `maitrise`) ;
 * « en_construction » est lu `en_decouverte`, le premier palier qui suit
 * `non_evalue`. `en_consolidation` n'est donc écrit par aucune notion. Toute la
 * correspondance tient à cette table : la changer ne demande ni migration ni
 * autre fichier, seulement un recalcul des projections.
 */
export const NIVEAU_DU_PALIER_NOTION = {
    nouveau: 'non_evalue',
    en_progression: 'en_decouverte',
    a_consolider: 'en_voie_de_maitrise',
    acquis: 'maitrise',
};
/** L'inverse : ce qu'un écran dit d'un `MaitriseNotion.palier` lu en base. */
export function palierNotionDuNiveau(niveau) {
    if (niveau === 'maitrise')
        return 'acquis';
    if (niveau === 'en_voie_de_maitrise' || niveau === 'en_consolidation')
        return 'a_consolider';
    if (niveau === 'en_decouverte')
        return 'en_progression';
    return 'nouveau';
}
/** Réussites qui font passer la recommandation au niveau suivant (MV-MAI-04). */
export const REUSSITES_DU_REPERE = 5;
/* -------------------------------------------------------------------------- */
/* 2. Le corrigé d'un exercice — `ContentItem.corrige`, validé aux deux bouts  */
/* -------------------------------------------------------------------------- */
const ecriture = z.string().trim().min(1).max(MAX_CARACTERES_REPONSE);
const texteRiche = z.string().trim().min(1);
/**
 * Une réponse fautive PRÉVUE qui n'est pas un type d'erreur. Le produit sépare
 * « ne pas comprendre » de « mal écrire » : un calcul qui a glissé est une
 * CAUSE (`inattention`), pas un code du catalogue ; un travail arrêté à
 * mi-chemin est un verdict `partiel`, pas une erreur. La maquette en faisait
 * deux « erreurs transversales » ; ici elles vivent dans le corrigé.
 */
export const SORTES_FAUTIVE_SANS_CODE = ['calcul_glisse', 'travail_incomplet'];
const fautiveSansCodeSchema = z
    .object({
    reponse: ecriture,
    sorte: z.enum(SORTES_FAUTIVE_SANS_CODE),
    cheminement: texteRiche,
})
    .strict();
const communCorrige = { fautivesSansCode: z.array(fautiveSansCodeSchema).optional() };
/** Une ligne d'une table de valeurs (`CAP-09`) : son nom en texte riche court, une écriture par colonne. */
const ligneDeTableSchema = z
    .object({
    nom: z.string().trim().min(1).max(60),
    valeurs: z.array(ecriture).min(COLONNES_DE_TABLE.min).max(COLONNES_DE_TABLE.max),
})
    .strict();
const caseDeTableSchema = z
    .object({ ligne: z.number().int().min(0), colonne: z.number().int().min(0) })
    .strict();
/** La table qu'un énoncé donne à lire, ou qu'une réponse fait remplir. */
export const tableDeValeursSchema = z
    .object({
    lignes: z.array(ligneDeTableSchema).min(LIGNES_DE_TABLE.min).max(LIGNES_DE_TABLE.max),
})
    .strict();
/**
 * Le NOM d'un vecteur-réponse (tranche T43) : `\vec{u}`, `\vec{AB}` — une ou deux
 * lettres sous la flèche, rien d'autre. C'est ce que l'écran écrit devant les
 * composantes, et ce que l'historique montre.
 */
export const NOM_DE_VECTEUR = /^\\vec\{[A-Za-z]{1,2}\}$/;
/** Ce que l'écran écrit devant un champ de couple : `x`, `y`, `a`, `t`… */
const nomDeComposante = z.string().trim().min(1).max(8);
/** Une écriture d'ensemble que `lireEnsemble` sait lire (en intervalles ou en inégalités sur `x`). */
const ecritureDEnsemble = z.string().trim().min(1).max(MAX_CARACTERES_REPONSE);
/* ── La réponse par un TRACÉ (Φ-MV-8, analyse 05, L-2 et L-3) ──────────────── */
/**
 * Les outils de l'atelier qu'un exercice de tracé peut ouvrir — « Choisir » est
 * toujours là. Ce sont des outils que la barre de l'atelier LIVRE DÉJÀ
 * (`OUTILS_BARRE`) : le manuel n'en ajoute aucun, il choisit parmi eux
 * (`CAP-07`, lot 1). Les trois premiers sont ceux d'origine (Φ-MV-8).
 */
export const OUTILS_TRACE = [
    'point',
    'droite',
    'segment',
    'cercle',
    'polygone',
    'fonction',
    'demi_plan',
    'vecteur',
    /* La MARQUE d'angle (tranche T6) : elle ne se mesure pas, elle montre l'angle construit. */
    'angle',
    /* Atelier de figures, L3 (F3) — CONSTRUIRE : la règle et le compas, puis les
       transformations. Le réducteur (`IdOutil`) et le moteur (`Definition`) les
       savaient déjà ; la barre de l'atelier les offre désormais. ADDITIF. */
    'compas',
    'milieu',
    'perpendiculaire',
    'parallele',
    'mediatrice',
    'bissectrice',
    'intersection',
    'translation',
    'rotation',
    'reflexion',
    'homothetie',
    /* Atelier de figures, L6 (F8) : ESQUISSER une courbe d'un geste ; le code la réduit en
       caractéristiques (sommet, zéros, sens, asymptotes). ADDITIF. */
    'esquisse',
];
/** Les outils de construction ouverts au palier L3 (F3) : ce que `methode` peut exclure ou demander. */
export const OUTILS_CONSTRUCTION_L3 = [
    'compas',
    'milieu',
    'perpendiculaire',
    'parallele',
    'mediatrice',
    'bissectrice',
    'intersection',
    'translation',
    'rotation',
    'reflexion',
    'homothetie',
];
/**
 * Les contraintes de `verifierConstruction` qu'un exercice du manuel sait
 * déclarer. Les cinq premières tracent une droite ou posent un point
 * (Φ-MV-8) ; les six suivantes ouvrent le cercle, le polygone, la courbe d'une
 * fonction, le demi-plan et le vecteur (`CAP-07`, lot 1). Toutes sont mesurées
 * par le vérificateur partagé, sans modèle.
 */
export const CONTRAINTES_TRACE = [
    'pente',
    'ordonnee_origine',
    'droite_par',
    'passe_par',
    'point_en',
    'sur',
    'rayon',
    'aire',
    'courbe_egale',
    'demi_plan',
    'vecteur_egal',
    /* Tranche T6 : la longueur d'un segment tracé, la mesure d'un angle par trois points. */
    'longueur',
    'angle',
    /* Tranche T17 : l'IMAGE d'une figure de l'énoncé par une translation, une
       réflexion ou une rotation ; et cinq constructions que le vérificateur
       mesurait déjà. */
    'image',
    'milieu',
    'perpendiculaire',
    'parallele',
    'mediatrice',
    'bissectrice',
    /* Tranche T42 : la fonction définie par parties, tracée partie par partie, bouts compris. */
    'parties_egales',
    /* Atelier de figures, L3 (F3) : quatre contraintes que le vérificateur
       mesurait déjà (`verifier.ts`), rallumées pour le manuel. ADDITIF. */
    'intersection',
    'equidistant',
    'isometrique',
    'semblable',
    /* Atelier de figures, L6 (F8) : les caractéristiques d'une courbe esquissée. ADDITIF. */
    'caracteristiques',
];
/** Ce qu'un tracé du manuel peut demander de produire (`CibleAttendue.genre`, restreint). */
export const GENRES_CIBLE_TRACE = [
    'point',
    'ligne',
    'droite',
    'segment',
    'cercle',
    'polygone',
    'fonction',
    'demi_plan',
    'vecteur',
    /* Tranche T42 : toutes les courbes bornées de l'élève, une fonction définie par parties. */
    'par_parties',
    /* Atelier de figures, L6 (F8) : une courbe esquissée d'un geste. */
    'esquisse',
];
const nombreFini = z.number().finite();
/**
 * Les paramètres d'une fonction à tracer (tranche T44) : des nombres — et,
 * pour une sinusoïde, sa `fonction` (« sin » ou « cos ») et un b ou un h
 * écrits exactement en π (« pi/4 », « 2pi/3 »), comme dans une figure
 * (`ParametresFonctionV2`). La famille juge le reste : `fonctionCanoniqueV2`,
 * au rejeu de la construction de référence, refuse un π hors de b et h, une
 * `fonction` hors de la sinusoïde.
 */
const parametresFonction = z.record(z.union([
    nombreFini,
    z
        .string()
        .trim()
        .min(1)
        .refine((v) => /^(sin|cos|sinus|cosinus)$/iu.test(v) || nombreOuPiV2(v) !== null, '« sin », « cos », ou un nombre écrit en π (« pi/4 »)'),
]));
/** Une cible de l'attendu, un point NOMMÉ de la figure d'énoncé, ou des coordonnées. */
const refTrace = z.union([
    z.object({ cible: z.string().min(1) }).strict(),
    z.object({ enonce: z.string().min(1) }).strict(),
    z.object({ x: nombreFini, y: nombreFini }).strict(),
]);
/**
 * `erreur` : l'erreur type que nomme cette contrainte quand elle est la
 * première manquée et qu'aucun tracé fautif prévu n'est reconnu. Un ALIAS dans
 * le dossier, un code stable en base.
 */
const erreurDeContrainte = { erreur: z.string().min(1).optional() };
/** L'objet TRACÉ que mesure une contrainte : toujours une cible de l'attendu. */
const refCible = z.object({ cible: z.string().min(1) }).strict();
/** Un objet de la figure d'ÉNONCÉ : un point par son nom (« A »), tout autre par son rang (« e:3 »). */
const refEnonce = z.object({ enonce: z.string().min(1) }).strict();
/** Une ligne : tracée (une cible) ou donnée par la figure d'énoncé. */
const refLigne = z.union([refCible, refEnonce]);
/**
 * Une transformation du plan, CALCULÉE par le vérificateur (`transformationRef`) :
 *
 *  - `translation` : ses composantes `x`, `y` ; ou deux points, `de` → `a` ;
 *  - `reflexion` : son `axe`, une ligne de la figure d'énoncé ;
 *  - `rotation` : son `centre` et son `angle` en degrés — POSITIF dans le sens
 *    antihoraire, NÉGATIF dans le sens horaire (un quart de tour horaire : −90) ;
 *  - `homothetie` : son `centre` et son `rapport`, STRICTEMENT POSITIF — entier
 *    (2), décimal (0,5 ; 1,5) ou fraction écrite en décimales (0.3333333333 pour
 *    un tiers). Le rapport négatif n'est pas du 1er cycle : il n'est pas ouvert.
 */
const transformationTraceSchema = z.union([
    z.object({ type: z.literal('translation'), x: nombreFini, y: nombreFini }).strict(),
    z.object({ type: z.literal('translation'), de: refTrace, a: refTrace }).strict(),
    z.object({ type: z.literal('reflexion'), axe: refEnonce }).strict(),
    z
        .object({
        type: z.literal('rotation'),
        centre: refTrace,
        angle: nombreFini.min(-360).max(360),
    })
        .strict(),
    /* Tranche T26 (F-86, 11 octobre 2026) : l'homothétie de rapport POSITIF. */
    z
        .object({
        type: z.literal('homothetie'),
        centre: refTrace,
        rapport: nombreFini.positive(),
    })
        .strict(),
]);
export const contrainteTraceSchema = z.discriminatedUnion('type', [
    z
        .object({ type: z.literal('pente'), ligne: refTrace, valeur: nombreFini })
        .extend(erreurDeContrainte)
        .strict(),
    z
        .object({ type: z.literal('ordonnee_origine'), ligne: refTrace, valeur: nombreFini })
        .extend(erreurDeContrainte)
        .strict(),
    z
        .object({ type: z.literal('droite_par'), ligne: refTrace, p1: refTrace, p2: refTrace })
        .extend(erreurDeContrainte)
        .strict(),
    z
        .object({ type: z.literal('passe_par'), ligne: refTrace, point: refTrace })
        .extend(erreurDeContrainte)
        .strict(),
    z
        .object({ type: z.literal('point_en'), point: refTrace, x: nombreFini, y: nombreFini })
        .extend(erreurDeContrainte)
        .strict(),
    /* ── `CAP-07`, lot 1 ── */
    /** Un point (donné, ou tracé) est SUR l'objet : une ligne, un cercle, le contour d'un polygone, une courbe. */
    z
        .object({ type: z.literal('sur'), point: refTrace, objet: refCible })
        .extend(erreurDeContrainte)
        .strict(),
    z
        .object({ type: z.literal('rayon'), cercle: refCible, valeur: z.number().positive() })
        .extend(erreurDeContrainte)
        .strict(),
    z
        .object({ type: z.literal('aire'), polygone: refCible, valeur: z.number().positive() })
        .extend(erreurDeContrainte)
        .strict(),
    /**
     * La courbe tracée est celle de la fonction : la famille et ses paramètres
     * (`fonctionCanoniqueV2`), et le domaine si la consigne en borne le tracé.
     * Seules les familles que l'élève sait tracer (`FAMILLES_TRACEES_V2`) — la
     * sinusoïde et la tangente comprises depuis la tranche T44, b et h en π.
     */
    z
        .object({
        type: z.literal('courbe_egale'),
        objet: refCible,
        famille: z.enum(FAMILLES_TRACEES_V2),
        parametres: parametresFonction,
        domaine: z.tuple([nombreFini, nombreFini]).optional(),
    })
        .extend(erreurDeContrainte)
        .strict(),
    /**
     * La fonction définie par parties (tranche T42) : chaque partie, sa famille,
     * ses paramètres, son intervalle et ses deux bouts (`plein`, `vide`, `aucun`).
     * La forme fine — chevauchement, deux bouts pleins au même x — est tenue par
     * `lirePartiesV2`, la lecture même de la figure `par_parties`.
     */
    z
        .object({
        type: z.literal('parties_egales'),
        objet: refCible,
        parties: z
            .array(z
            .object({
            famille: z.enum(FAMILLES_TRACEES_V2),
            parametres: parametresFonction,
            domaine: z.tuple([nombreFini, nombreFini]),
            bouts: z.tuple([z.enum(BOUTS_V2), z.enum(BOUTS_V2)]),
        })
            .strict())
            .min(PARTIES_MIN_V2)
            .max(PARTIES_MAX_V2),
    })
        .extend(erreurDeContrainte)
        .strict(),
    /**
     * Le demi-plan hachuré : sa frontière `y = a·x + b` (jamais verticale), le
     * côté, et si la frontière en est exclue (`stricte` : inégalité stricte,
     * frontière en pointillé).
     */
    z
        .object({
        type: z.literal('demi_plan'),
        objet: refCible,
        famille: z.literal('lineaire'),
        parametres: z.object({ a: nombreFini, b: nombreFini.optional() }).strict(),
        cote: z.enum(['dessus', 'dessous']),
        stricte: z.boolean(),
    })
        .extend(erreurDeContrainte)
        .strict(),
    /** Les composantes du vecteur tracé — où qu'il soit posé. */
    z
        .object({ type: z.literal('vecteur_egal'), objet: refCible, x: nombreFini, y: nombreFini })
        .extend(erreurDeContrainte)
        .strict(),
    /* ── Tranche T6 ── */
    /** La longueur du segment tracé, en unités du repère (à 2 % près, la tolérance du vérificateur). */
    z
        .object({ type: z.literal('longueur'), segment: refCible, valeur: z.number().positive() })
        .extend(erreurDeContrainte)
        .strict(),
    /**
     * L'angle géométrique de sommet `sommet`, entre les demi-droites vers `de` et
     * vers `a`, en degrés (de 0 à 180 ; à 2° près). Trois POINTS : tracés, donnés
     * par la figure d'énoncé, ou écrits par leurs coordonnées.
     */
    z
        .object({
        type: z.literal('angle'),
        sommet: refTrace,
        de: refTrace,
        a: refTrace,
        valeur: z.number().min(0).max(180),
    })
        .extend(erreurDeContrainte)
        .strict(),
    /* ── Tranche T17 ── */
    /**
     * Le tracé EST l'image d'un objet de la figure d'énoncé par la
     * transformation : un polygone (ses sommets, dans n'importe quel ordre de
     * parcours), un point, une ligne ou un cercle. `source` : le point nommé
     * (« A ») ou l'objet par son rang dans `figure.objets`, compté de zéro
     * (« e:3 »). Un tracé qui n'est pas l'image est une ERREUR, quoi que
     * tiennent les autres contraintes (`corrigerTrace`).
     *
     * SANS correspondance des sommets : l'atelier ne demande pas à l'élève de
     * nommer ses points, on ne peut donc pas exiger que « A′ » soit l'image de A.
     */
    z
        .object({
        type: z.literal('image'),
        objet: refCible,
        source: refEnonce,
        transformation: transformationTraceSchema,
    })
        .extend(erreurDeContrainte)
        .strict(),
    /** Le point est le milieu de [de, a]. */
    z
        .object({ type: z.literal('milieu'), point: refTrace, de: refTrace, a: refTrace })
        .extend(erreurDeContrainte)
        .strict(),
    /** La ligne tracée est perpendiculaire à `a` : une ligne de l'énoncé (« e:4 ») ou une autre cible. */
    z
        .object({ type: z.literal('perpendiculaire'), ligne: refCible, a: refLigne })
        .extend(erreurDeContrainte)
        .strict(),
    z
        .object({ type: z.literal('parallele'), ligne: refCible, a: refLigne })
        .extend(erreurDeContrainte)
        .strict(),
    /** La ligne tracée est la médiatrice de [de, a] : perpendiculaire, et par le milieu. */
    z
        .object({ type: z.literal('mediatrice'), ligne: refCible, de: refTrace, a: refTrace })
        .extend(erreurDeContrainte)
        .strict(),
    /** La ligne tracée est la bissectrice INTÉRIEURE de l'angle de sommet `sommet`, entre [sommet, de) et [sommet, a). */
    z
        .object({
        type: z.literal('bissectrice'),
        ligne: refCible,
        sommet: refTrace,
        de: refTrace,
        a: refTrace,
    })
        .extend(erreurDeContrainte)
        .strict(),
    /* ── Atelier de figures, L6 (F8) ── */
    /**
     * LES CARACTÉRISTIQUES d'une courbe esquissée (sa cible, de genre
     * `esquisse`) : chaque champ écrit est vérifié, à une DEMI-CASE près sur un
     * point remarquable — le sommet, l'ouverture, les zéros, le sens de
     * variation de gauche à droite, les asymptotes. La référence (une
     * `fonction`) est réduite par le même code : elle doit les tenir.
     */
    z
        .object({
        type: z.literal('caracteristiques'),
        objet: refCible,
        sommet: z.object({ x: nombreFini, y: nombreFini }).strict().optional(),
        ouverture: z.enum(['haut', 'bas']).optional(),
        zeros: z.array(nombreFini).max(4).optional(),
        sens: z
            .array(z.enum(['croissante', 'decroissante']))
            .min(1)
            .max(4)
            .optional(),
        asymptotes: z
            .object({ horizontale: nombreFini.optional(), verticale: nombreFini.optional() })
            .strict()
            .optional(),
    })
        .extend(erreurDeContrainte)
        .strict(),
    /* ── Atelier de figures, L3 (F3) ── */
    /**
     * Le point est À L'INTERSECTION des deux objets : des lignes ou des cercles,
     * tracés (des cibles) ou donnés par la figure d'énoncé (« e:3 »).
     */
    z
        .object({
        type: z.literal('intersection'),
        point: refTrace,
        objets: z.tuple([refLigne, refLigne]),
    })
        .extend(erreurDeContrainte)
        .strict(),
    /** Le point est à ÉGALE DISTANCE de `de` et de `a` (à 2 % près) : il est sur la médiatrice. */
    z
        .object({ type: z.literal('equidistant'), point: refTrace, de: refTrace, a: refTrace })
        .extend(erreurDeContrainte)
        .strict(),
    /**
     * Le polygone tracé est ISOMÉTRIQUE au polygone `source` de l'énoncé : mêmes
     * côtés, mêmes angles, dans n'importe quel ordre de parcours — où qu'il soit
     * posé, quelle que soit l'isométrie.
     */
    z
        .object({ type: z.literal('isometrique'), objet: refCible, source: refEnonce })
        .extend(erreurDeContrainte)
        .strict(),
    /**
     * Le polygone tracé est SEMBLABLE au polygone `source` : angles égaux, côtés
     * proportionnels — au `rapport` donné, ou à un rapport constant s'il est absent.
     */
    z
        .object({
        type: z.literal('semblable'),
        objet: refCible,
        source: refEnonce,
        rapport: z.number().finite().positive().optional(),
    })
        .extend(erreurDeContrainte)
        .strict(),
]);
const cibleTraceSchema = z
    .object({
    id: z.string().min(1),
    genre: z.enum(GENRES_CIBLE_TRACE),
    nom: z.string().min(1).optional(),
})
    .strict();
/** Un objet de la construction de référence — sa forme fine est tenue par `griefsDocument`. */
const objetTraceSchema = z
    .object({
    id: z.string().min(1),
    nom: z.string().min(1).optional(),
    def: z.record(z.unknown()),
})
    .strict();
/**
 * Un TRACÉ FAUTIF PRÉVU : la construction que donne une erreur type, dite par
 * les contraintes qu'elle tient (« pente −2 et ordonnée à l'origine 3 »). Son
 * erreur et son cheminement sont ceux de la réponse fautive de même `reponse`
 * (`ItemErreurType.reponseFautive`).
 */
const traceFautifSchema = z
    .object({ reponse: ecriture, contraintes: z.array(contrainteTraceSchema).min(1) })
    .strict();
/** Un pas de graduation : un nombre, ou une écriture exacte en π (« pi/2 »), positif. */
const pasTrace = z.union([
    z.number().positive(),
    z
        .string()
        .trim()
        .min(1)
        .refine((v) => (nombreOuPiV2(v) ?? 0) > 0, 'un pas positif : un nombre, ou un multiple de π (« pi/2 »)'),
]);
/** La fenêtre d'un repère, telle que la banque l'écrit (`RepereV2Dto`). */
const repereTraceSchema = z
    .object({
    xMin: nombreFini,
    xMax: nombreFini,
    yMin: nombreFini,
    yMax: nombreFini,
    axes: z.boolean().optional(),
    grille: z.boolean().optional(),
    /* Un pas écrit en π (tranche T44 : « pi/2 ») gradue l'axe en π/2, π, 3π/2… */
    pas: z.union([pasTrace, z.object({ x: pasTrace, y: pasTrace }).strict()]).optional(),
})
    .strict();
/**
 * UN CURSEUR DU MODE EXPLORER (atelier de figures, lot L2, F1) : la lettre
 * qu'il règle, ses bornes, son pas et sa valeur de départ. Bornes et départ
 * sont des multiples du pas — l'élève ne peut poser que ce que le curseur
 * aimante. Le départ est NEUTRE : jamais la réponse (`griefsDuReglage`).
 */
const curseurReglageSchema = z
    .object({
    /** « a », « h », « k »… — un paramètre de la famille ; « Z » pour une droite baladeuse. */
    nom: z.string().regex(/^[a-zA-Z]$/u, 'une seule lettre'),
    min: nombreFini,
    max: nombreFini,
    pas: z.number().finite().positive(),
    depart: nombreFini,
})
    .strict();
/**
 * LES PARAMÈTRES RÉGLABLES d'un tracé (atelier de figures, lot L2, F1 ;
 * SPEC-REPRESENTATION-VISUELLE § 6.5, G5). Présent, l'élève ne trace pas dans
 * l'atelier : il règle la courbe aux curseurs, en mode Explorer (rien n'est
 * noté) puis en mode Répondre (les curseurs reviennent au départ). Ce qu'il
 * soumet est une construction ordinaire — une `fonction` de la famille, aux
 * paramètres réglés — que `jugerTrace` corrige comme tout tracé
 * (`courbe_egale`). ADDITIF : absent, rien ne change.
 *
 * `baladeuse` : la DROITE BALADEUSE d'une optimisation, x·X + y·Y = Z. Un seul
 * curseur, « Z » ; la droite tracée est y = (Z − x·X) / y, une `lineaire`.
 */
export const reglageTraceSchema = z
    .object({
    /** La cible (de genre `fonction`) que les curseurs tracent. */
    objet: z.string().min(1),
    famille: z.enum(FAMILLES_TRACEES_V2),
    /** La règle écrite au-dessus de la figure : « \\(y = a(x - h)^2 + k\\) ». */
    formule: texteRiche.optional(),
    /** Les paramètres que l'énoncé donne, qui ne bougent pas. */
    fixes: z.record(nombreFini).optional(),
    curseurs: z.array(curseurReglageSchema).min(1).max(4),
    baladeuse: z.object({ x: nombreFini, y: nombreFini }).strict().optional(),
    /** Le tracé borné, quand la contrainte l'est. */
    domaine: z.tuple([nombreFini, nombreFini]).optional(),
})
    .strict();
/**
 * LA MÉTHODE IMPOSÉE d'une construction (atelier de figures, L3, F3 ;
 * arbitrage A2 de l'atelier) : « Construis la médiatrice de [AB] AU COMPAS ».
 *
 *  - `outilsExclus` : les outils que la consigne interdit. L'atelier ne les
 *    OFFRE pas (l'import refuse un outil à la fois offert et exclu) ; si un
 *    document en emploie quand même un (un brouillon d'avant, un autre
 *    appareil), le vérificateur le voit (`VerificationConstruction.methode`) et
 *    le verdict descend à « partiel », avec la phrase qui le dit ;
 *  - `outilDemande` : l'outil que la consigne demande, pour cette phrase :
 *    « Construite avec l'outil Médiatrice, la consigne demandait le compas. »
 *    Il est offert.
 *
 * ADDITIF : absente, rien ne change.
 */
export const methodeTraceSchema = z
    .object({
    outilsExclus: z.array(z.enum(OUTILS_TRACE)).min(1),
    outilDemande: z.enum(OUTILS_TRACE).optional(),
})
    .strict();
/**
 * LA COURBE PAR POINTS OU ESQUISSÉE (atelier de figures, L6, F8 ; CONCEPTION
 * § 2) : au lieu de l'atelier, une zone où l'élève
 *
 *  - `points` : POSE les points d'une table (au doigt, aimantés à la
 *    demi-case, ou par leurs coordonnées) — et le trait se dessine de point
 *    en point. Chaque point se juge par `point_en` ;
 *  - `esquisse` : ESQUISSE la courbe d'un geste ; le code la réduit en
 *    caractéristiques, que l'élève confirme, et `caracteristiques` les juge.
 *
 * ADDITIF : absente, le tracé s'ouvre dans l'atelier, comme avant.
 */
export const courbeTraceSchema = z
    .object({
    mode: z.enum(['points', 'esquisse']),
    /** `points` : relier les points par une courbe lisse (défaut) ou des segments. */
    relier: z.enum(['courbe', 'segments']).optional(),
})
    .strict();
/**
 * CE QUE L'IMPORT REFUSE d'une courbe par points ou esquissée (L6, F8) :
 *  - avec des curseurs (`reglage`) : l'un ou l'autre ;
 *  - `points` : d'autres outils que « point », d'autres cibles que des
 *    points (deux au moins), d'autres contraintes que `point_en` ;
 *  - `esquisse` : d'autres outils que « esquisse », pas exactement UNE cible
 *    de genre `esquisse`, des contraintes qui ne sont pas des
 *    `caracteristiques` de cette cible, une référence qui n'est pas UNE fonction ;
 *  - sans `courbe` en mode esquisse : l'outil, la cible ou la contrainte d'une esquisse.
 */
export function griefsDeCourbe(c) {
    const g = [];
    const esquisse = c.courbe?.mode === 'esquisse';
    if (!esquisse) {
        if (c.outils.includes('esquisse'))
            g.push('l’outil « esquisse » s’offre avec `courbe: { mode: "esquisse" }`');
        if (c.cibles.some((x) => x.genre === 'esquisse'))
            g.push('une cible `esquisse` demande `courbe: { mode: "esquisse" }`');
        if (c.contraintes.some((x) => x.type === 'caracteristiques')) {
            g.push('la contrainte `caracteristiques` juge une esquisse : `courbe: { mode: "esquisse" }`');
        }
    }
    if (c.courbe === undefined)
        return g;
    if (c.reglage !== undefined)
        g.push('une courbe se règle aux curseurs OU se pose par points ou s’esquisse : pas les deux');
    if (c.courbe.mode === 'points') {
        if (c.outils.length !== 1 || c.outils[0] !== 'point')
            g.push('une courbe par points s’offre avec le seul outil « point »');
        if (c.cibles.length < 2 || c.cibles.some((x) => x.genre !== 'point')) {
            g.push('une courbe par points demande deux points au moins (des cibles de genre `point`)');
        }
        if (c.contraintes.some((x) => x.type !== 'point_en'))
            g.push('chaque point d’une courbe par points se juge par `point_en`');
        return g;
    }
    if (c.outils.length !== 1 || c.outils[0] !== 'esquisse')
        g.push('une esquisse s’offre avec le seul outil « esquisse »');
    const cibles = c.cibles.filter((x) => x.genre === 'esquisse');
    if (cibles.length !== 1 || c.cibles.length !== 1)
        g.push('une esquisse a UNE cible, de genre `esquisse`');
    if (c.contraintes.length === 0 || c.contraintes.some((x) => x.type !== 'caracteristiques')) {
        g.push('une esquisse se juge par ses `caracteristiques`, et par elles seules');
    }
    const fonctions = c.solution.filter((o) => o.def.type === 'fonction');
    if (fonctions.length !== 1)
        g.push('la référence d’une esquisse est UNE fonction (`solution`)');
    return g;
}
/**
 * LES ERREURS DE GESTE qu'aucune contrainte ne dit, parce qu'elles empêchent
 * de tracer la cible (atelier de figures, L3, F3 ; CONCEPTION § 3) :
 *
 *  - `arcsDisjoints` : l'ouverture du compas est trop petite, deux arcs ne se
 *    coupent pas — il n'y a pas d'intersection où faire passer la médiatrice.
 *    Le code le reconnaît sur les cercles tracés au compas (`arcsQuiNeSeCoupentPas`).
 *
 * La valeur est l'erreur du catalogue (un ALIAS dans le dossier, un code
 * stable en base). ADDITIF : absent, rien n'est reconnu de plus.
 */
export const erreursDeConstructionSchema = z
    .object({ arcsDisjoints: z.string().min(1).optional() })
    .strict();
const champsTrace = {
    nature: z.literal('trace'),
    /** Atelier de figures, L2 (F1) — ADDITIF : la courbe se règle aux curseurs. */
    reglage: reglageTraceSchema.optional(),
    /** Atelier de figures, L6 (F8) — ADDITIF : la courbe se pose par points, ou s'esquisse. */
    courbe: courbeTraceSchema.optional(),
    /**
     * Le repère de départ, quand l'exercice n'a pas de figure d'énoncé : une
     * page vide où tracer. Avec une figure (des points donnés, une droite), c'est
     * ELLE qui porte le repère, et ce champ est absent — jamais les deux.
     */
    repere: repereTraceSchema.optional(),
    /** Les seuls outils que l'atelier propose pour cet exercice. */
    outils: z.array(z.enum(OUTILS_TRACE)).min(1),
    /** Atelier de figures, L3 (F3) — ADDITIF : la méthode que la consigne impose. */
    methode: methodeTraceSchema.optional(),
    /** Atelier de figures, L3 (F3) — ADDITIF : les erreurs de geste que le code reconnaît. */
    erreursDeConstruction: erreursDeConstructionSchema.optional(),
    cibles: z.array(cibleTraceSchema).min(1),
    /** Toutes requises. Au vocabulaire de `verifierConstruction`. */
    contraintes: z.array(contrainteTraceSchema).min(1),
    /** La construction de référence, sur le repère de départ. */
    solution: z.array(objetTraceSchema).min(1),
};
/**
 * LE BROUILLON DE FIGURE PERMIS (atelier de figures, L4, F5 ; décision 4 du
 * fondateur, 17 octobre 2026) — `ContentItem.enonce.brouillonDeFigure`.
 *
 * En situation-problème et au niveau Raisonner, l'élève OUVRE l'atelier pour
 * PENSER : sa figure est un DOCUMENT DE CONSTRUCTION (quelques Ko de JSON),
 * gardé avec l'essai, joint à la démarche, visible du tuteur — sans image,
 * jamais lu par un modèle, JAMAIS NOTÉ.
 *
 *  - `outils` : les outils que l'atelier offre pour ce brouillon ;
 *  - `repere` : la page où dessiner quand l'énoncé n'a pas de figure du plan.
 *    Avec une figure du plan, c'est ELLE qui porte le repère (et elle vient en
 *    calque) : ce champ est absent — jamais les deux (l'import le contrôle).
 *
 * ADDITIF : absent, rien ne change.
 */
export const brouillonDeFigureSchema = z
    .object({
    outils: z.array(z.enum(OUTILS_TRACE)).min(1),
    repere: repereTraceSchema.optional(),
})
    .strict();
/**
 * LA PARTIE « CONSTRUCTION » D'UNE SITUATION-PROBLÈME (atelier de figures,
 * L4, F5) — `ContentItem.corrige.partieConstruite` d'une réponse `demarche`.
 *
 * Une question de la SP demande un TRACÉ (« b) Construis la médiatrice de
 * [AB] pour placer le puits. ») : elle se corrige par le CODE (`jugerTrace`),
 * sans modèle (décision 5). Les autres questions gardent leur voie — la
 * démarche lue sur ses trois critères (P8, doc 22 v2.19), INCHANGÉE.
 *
 *  - `consigne` : la question de la SP qui demande le tracé, telle que l'élève
 *    la lit au-dessus de l'atelier ;
 *  - `critere` : le critère de la SP que cette partie INFORME (défaut
 *    proposé : `mobilisation`, « Utiliser les bonnes lois »). Il est AFFICHÉ
 *    avec la correction de la partie ; il ne change PAS les points (règle
 *    prudente en attendant le fondateur : `L4-FORMAT.md` § 4) ;
 *  - `reponse` : un corrigé de tracé ordinaire — outils, cibles, contraintes,
 *    construction de référence, méthode, erreurs de geste (sans curseurs).
 *
 * ADDITIF : absente, la SP se corrige exactement comme avant.
 */
export const partieConstruiteSchema = z
    .object({
    consigne: texteRiche,
    critere: z.enum(CRITERES_SITUATION_PROBLEME).optional(),
    reponse: z.object(champsTrace).strict(),
})
    .strict();
/**
 * La partie construite dans le FORMAT D'ÉCHANGE (`exercices/SP.json`) : la même,
 * plus le contrôle du vérificateur par calcul (`controle.attendu` : le tracé de
 * la cible, dans la langue du contrôle d'un tracé) — la SP garde son propre
 * `controle` pour sa démarche. Le contrôle n'entre pas en base.
 */
export const partieConstruiteEchangeSchema = partieConstruiteSchema
    .extend({
    controle: z.object({ attendu: z.string().trim().min(1) }).strict(),
})
    .strict();
/**
 * LA FICHE DE CORRECTION d'une réponse lue par le modèle (analyse 06, § 4.4 ;
 * `corrections/*.json` du dossier). Ce que le correcteur doit savoir et qu'aucun
 * autre champ ne porte : ce qui est AUSSI juste et pourquoi, ce que chaque
 * erreur attendue donne à lire dans une réponse et ce qu'on en dit à l'élève,
 * ce qui ne change jamais un verdict. La grille, elle, vit dans
 * `ContentItem.grilleCorrection`.
 *
 * Portée par le corrigé d'une réponse rédigée (`texte`) et d'un vrai ou faux
 * dont la justification est lue (français). Jamais servie avant la correction.
 */
const ficheDeCorrection = {
    /** « une phrase », « trois courts paragraphes » — dit à l'élève sous le champ. */
    formeReponse: z.string().trim().min(1).max(160).optional(),
    reponsesAcceptables: z
        .array(z.object({ reponse: texteRiche, pourquoi: texteRiche }).strict())
        .optional(),
    /** `erreur` : le code STABLE (un alias dans le dossier). */
    erreursAttendues: z
        .array(z
        .object({ erreur: z.string().min(1), manifestation: texteRiche, retroaction: texteRiche })
        .strict())
        .optional(),
    /** Le hors-sujet commun au chapitre, puis celui de l'exercice. */
    horsSujet: z.array(texteRiche).optional(),
};
/**
 * Le contrat de `ContentItem.corrige` pour un exercice du manuel, par nature.
 * L'import l'écrit après l'avoir validé ; la voie de correction le relit et le
 * revalide avant de s'en servir (I-9 : « aux deux bouts »).
 */
export const corrigeExerciceSchema = z.discriminatedUnion('nature', [
    z
        .object({
        nature: z.literal('nombre'),
        valeur: z.number().finite(),
        /** Écart absolu admis ; absent : `1e-9` relatif. */
        tolerance: z.number().positive().optional(),
        /** Écrite à droite du champ ; ignorée à la comparaison. */
        unite: z.string().trim().min(1).optional(),
        ...communCorrige,
    })
        .strict(),
    z
        .object({
        nature: z.literal('expression'),
        /** La forme canonique, celle qu'on affiche. */
        latex: ecriture,
        /**
         * La liste FERMÉE des autres écritures acceptées. Une écriture de même
         * valeur hors de cette liste n'a pas la forme demandée (`partiel`).
         */
        equivalents: z.array(ecriture),
        /**
         * `libre` (`CAP-10`) : la FORME n'est pas demandée — toute écriture de
         * même valeur est juste (le calcul formel le dit), sauf l'écriture même
         * de l'énoncé et les réponses fautives prévues. ABSENTE : la forme est
         * exigée, par la liste fermée ci-dessus, comme avant.
         */
        forme: z.literal('libre').optional(),
        ...communCorrige,
    })
        .strict(),
    /**
     * UN COUPLE (`CAP-10`) : la solution d'un système, les coordonnées d'un
     * point. Deux écritures, comparées chacune par sa VALEUR. `noms` : ce que
     * l'écran écrit devant chaque champ (défaut : `x` et `y`).
     */
    z
        .object({
        nature: z.literal('couple'),
        x: ecriture,
        y: ecriture,
        noms: z.tuple([nomDeComposante, nomDeComposante]).optional(),
        ...communCorrige,
    })
        .strict(),
    /**
     * UN VECTEUR PAR SES COMPOSANTES (tranche T43, P-VECTEUR) : `nom` (`\vec{AB}`),
     * et ses composantes `x` et `y`, chacune comparée par sa VALEUR — la lecture du
     * couple, mot pour mot. L'élève remplit deux champs, entre « \vec{AB} = ( » et
     * « ) » ; la réponse fautive prévue s'écrit « (a ; b) », comme celle d'un couple.
     */
    z
        .object({
        nature: z.literal('vecteur'),
        nom: z.string().trim().regex(NOM_DE_VECTEUR, {
            message: 'le nom d’un vecteur s’écrit \\vec{u} ou \\vec{AB} (une ou deux lettres)',
        }),
        x: ecriture,
        y: ecriture,
        ...communCorrige,
    })
        .strict(),
    /**
     * UN INTERVALLE (`CAP-10`), ou une réunion d'intervalles, écrit à la
     * française : `[2 ; 5[`, `]-∞ ; 1[ ∪ [4 ; +∞[`, `∅`, `ℝ`. La réponse doit
     * être EN INTERVALLES : le même ensemble dit en inégalités a la bonne valeur
     * sans la forme demandée.
     */
    z
        .object({ nature: z.literal('intervalle'), ensemble: ecritureDEnsemble, ...communCorrige })
        .strict(),
    /**
     * L'ENSEMBLE-SOLUTION d'une inéquation à une variable (`CAP-10`). L'élève
     * répond en inégalités (`x > 3`, `x < 1 ou x ≥ 4`), en intervalles, ou par
     * « aucune solution » / « tous les réels » : les trois écritures du même
     * ensemble sont la même réponse.
     */
    z
        .object({
        nature: z.literal('ensemble_solution'),
        variable: z.string().regex(/^[a-z]$/, 'une lettre minuscule'),
        ensemble: ecritureDEnsemble,
        /**
         * Tranche T45 : l'énoncé demande ses solutions ARRONDIES à tant de
         * décimales. Sans lui, la valeur exacte est attendue (`\dfrac{\pi}{6}`,
         * jamais `0{,}52`).
         */
        arrondi: z.number().int().min(0).max(6).optional(),
        ...communCorrige,
    })
        .strict(),
    /**
     * UNE TABLE DE VALEURS À REMPLIR (`CAP-09`) : la table COMPLÈTE, et les cases
     * que l'élève doit écrire. Chaque case est jugée seule, par sa valeur ; la
     * réponse fautive prévue d'une table s'écrit comme la réponse de l'élève —
     * `1:2=6 ; 1:3=8` (`ecrireCases`).
     */
    z
        .object({
        nature: z.literal('table'),
        lignes: z.array(ligneDeTableSchema).min(LIGNES_DE_TABLE.min).max(LIGNES_DE_TABLE.max),
        aRemplir: z.array(caseDeTableSchema).min(1),
        ...communCorrige,
    })
        .strict(),
    /**
     * UNE MATRICE À REMPLIR (tranche T41) : la matrice attendue, ligne par ligne,
     * une écriture par case. L'élève reçoit la grille vide, de mêmes dimensions,
     * et la remplit en entier ; chaque case est jugée seule, par sa valeur. La
     * réponse fautive prévue s'écrit comme une matrice de l'énoncé,
     * `\begin{pmatrix} … \end{pmatrix}`, de mêmes dimensions.
     */
    z
        .object({
        nature: z.literal('matrice'),
        lignes: z
            .array(z.array(ecriture).min(COLONNES_DE_MATRICE.min).max(COLONNES_DE_MATRICE.max))
            .min(LIGNES_DE_MATRICE.min)
            .max(LIGNES_DE_MATRICE.max)
            .refine((l) => l.every((x) => x.length === l[0]?.length), {
            message: 'toutes les lignes d’une matrice ont le même nombre de cases',
        }),
        delimiteur: z.enum(['parentheses', 'crochets']).optional(),
        ...communCorrige,
    })
        .strict(),
    /**
     * UN SOLIDE QU'ON TOURNE (atelier de figures, L6, F10) : le polyèdre, et ce
     * qu'on en fait — MARQUER un élément (son identité est jugée exactement) ou
     * DÉVELOPPER, compléter un patron (le code juge le pliage). La réponse
     * fautive prévue d'une marque s'écrit comme celle de l'élève : `marque=apotheme`.
     */
    z
        .object({
        nature: z.literal('solide'),
        solide: solideSchema,
        tache: tacheSolideSchema,
        vue: vueSolideSchema.optional(),
        ...communCorrige,
    })
        .strict(),
    /**
     * UN DIAGRAMME STATISTIQUE À CONSTRUIRE (`CAP-08`) : le diagramme COMPLET, et
     * les rangs des éléments que l'élève doit donner (`elementsDe` : une bande,
     * une classe, l'un des cinq nombres, l'ordonnée d'un point). Chaque élément
     * est jugé seul. La réponse fautive prévue s'écrit comme celle de l'élève :
     * `0:3=5 ; 0:4=2` (rang de l'élément, puis sa valeur).
     */
    z
        .object({
        nature: z.literal('diagramme'),
        diagramme: diagrammeSchema,
        /* Vide quand l'exercice ne demande QUE la droite ou le graphe (L5) : l'import le contrôle. */
        aDonner: z.array(z.number().int().min(0)),
        /** Atelier de figures, L5 (F7) : le pas d'une barre ou d'un point qu'on glisse. */
        aimant: aimantSchema.optional(),
        /** Atelier de figures, L5 (F7) : une droite à tracer à l'œil sur le nuage, et sa bande. */
        droite: droiteALOeilSchema.optional(),
        /** Atelier de figures, L5 (F9) : ce que l'élève fait du graphe. */
        graphe: tacheDeGrapheSchema.optional(),
        ...communCorrige,
    })
        .strict(),
    z.object({ nature: z.literal('choix'), bonne: z.string().min(1), ...communCorrige }).strict(),
    z
        .object({
        nature: z.literal('vrai_faux'),
        bonne: z.boolean(),
        ...ficheDeCorrection,
        ...communCorrige,
    })
        .strict(),
    z
        .object({
        nature: z.literal('demarche'),
        /** Une phrase du professeur, texte riche. */
        resultatAttendu: texteRiche,
        /** Atelier de figures, L4 (F5) — ADDITIF : la partie « construction » d'une SP. */
        partieConstruite: partieConstruiteSchema.optional(),
        ...communCorrige,
    })
        .strict(),
    z
        .object({
        ...champsTrace,
        tracesFautifs: z.array(traceFautifSchema).optional(),
        ...communCorrige,
    })
        .strict(),
    z
        .object({
        nature: z.literal('texte'),
        /** La réponse de référence : UNE des bonnes réponses, jamais la seule. */
        resultatAttendu: texteRiche,
        ...ficheDeCorrection,
        ...communCorrige,
    })
        .strict(),
]);
/**
 * Ce qu'un schéma ne voit pas d'une réponse fermée de `CAP-10` : un ensemble
 * que `lireEnsemble` ne sait pas lire, un intervalle écrit en inégalités, une
 * réponse fautive qui n'est ni un couple ni un ensemble. L'import le refuse
 * (`valider-exercices.ts`) ; la liste est vide pour toute autre nature.
 */
export function griefsDeReponseFermee(reponse, fautives) {
    const griefs = [];
    if (reponse.nature === 'couple' || reponse.nature === 'vecteur') {
        for (const cle of ['x', 'y']) {
            if (/[;]/.test(reponse[cle]) || lireCouple(reponse[cle]) !== null) {
                griefs.push({
                    chemin: `reponse.${cle}`,
                    message: 'une composante est UNE écriture, pas un couple',
                });
            }
        }
        fautives.forEach((f, k) => {
            if (lireCouple(f) === null) {
                griefs.push({
                    chemin: `reponsesFautives[${k}].reponse`,
                    message: reponse.nature === 'vecteur'
                        ? 'la réponse fautive d’un vecteur s’écrit « (a ; b) », ses composantes séparées par un point-virgule'
                        : 'la réponse fautive d’un couple s’écrit « (a ; b) », avec un point-virgule',
                });
            }
        });
        return griefs;
    }
    if (reponse.nature !== 'intervalle' && reponse.nature !== 'ensemble_solution')
        return griefs;
    const variable = reponse.nature === 'ensemble_solution' ? reponse.variable : 'x';
    /* Tranche T45 : un ensemble-solution peut être en π, ou périodique. */
    const lisible = (e) => reponse.nature === 'ensemble_solution'
        ? lireEnsembleSolution(e, variable) !== null
        : lireEnsemble(e, variable) !== null;
    const lu = lireEnsemble(reponse.ensemble, variable);
    /* Un ensemble exact (T45) n'a ni crochet ni intervalle à contrôler : il suffit qu'il se lise. */
    if (lu === null && !lisible(reponse.ensemble)) {
        griefs.push({
            chemin: 'reponse.ensemble',
            message: reponse.nature === 'ensemble_solution'
                ? `ensemble illisible : en intervalles ([2 ; 5[, ]-∞ ; 3], ∅, ℝ), en inégalités sur « ${variable} », en extension ({-3 ; 7}, {\\dfrac{\\pi}{6} ; \\dfrac{5\\pi}{6}}) ou en familles (${variable} = \\dfrac{\\pi}{6} + 2k\\pi ou …)`
                : `ensemble illisible : en intervalles ([2 ; 5[, ]-∞ ; 3], ∅, ℝ) ou en inégalités sur « ${variable} », à bornes numériques`,
        });
    }
    else if (lu === null) {
        // lisible en π ou en familles : rien de plus à contrôler
    }
    else if (lu.defaut !== undefined) {
        griefs.push({ chemin: 'reponse.ensemble', message: 'un crochet est fermé sur l’infini' });
    }
    else if (reponse.nature === 'intervalle' && lu.ecriture !== 'intervalle') {
        griefs.push({
            chemin: 'reponse.ensemble',
            message: 'la réponse d’un exercice « intervalle » s’écrit en intervalles : [2 ; 5[, ]-∞ ; 3], ∅, ℝ',
        });
    }
    fautives.forEach((f, k) => {
        if (!lisible(f)) {
            griefs.push({
                chemin: `reponsesFautives[${k}].reponse`,
                message: `réponse fautive illisible comme ensemble (variable « ${variable} »)`,
            });
        }
    });
    return griefs;
}
/** La réponse `trace` du format d'échange : le corrigé, sans ses tracés fautifs (ils sont dans `reponsesFautives`). */
export const reponseTraceEchangeSchema = z.object(champsTrace).strict();
/**
 * Le REPÈRE DE DÉPART d'un tracé : celui de la figure d'énoncé quand il y en a
 * une, sinon celui que le corrigé déclare. `null` : l'exercice n'en a aucun, il
 * ne peut ni s'ouvrir ni se corriger (l'import le refuse).
 */
export function repereDeTrace(corrige, enonce) {
    if (enonce === null)
        return corrige.repere ?? null;
    /* Un axe gradué en π le reste (tranche T44) : l'atelier gradue en π/2, π… */
    return repereV2VersDto(enonce.repere);
}
/**
 * Le DOCUMENT d'un tracé du manuel, posé sur son repère de départ : celui que
 * l'atelier ouvre vide, et celui de la construction de référence. Avec une
 * figure d'énoncé, le calque est de source `partie` — la figure est propre à
 * l'exercice, et c'est le serveur qui la relit en banque.
 */
export function documentDeTrace(repere, options) {
    return {
        version: 1,
        espace: 'plan',
        statut: 'reponse',
        repere,
        unite: null,
        calque: { source: options.calque ? 'partie' : null, visible: options.calque },
        objets: options.objets ?? [],
    };
}
/**
 * L'attendu que lit `verifierConstruction`, tiré du corrigé d'un tracé. La
 * MÉTHODE imposée (L3) passe quand le corrigé la porte : `outilsExclus` est ce
 * que le vérificateur lit (arbitrage A2).
 */
export function attenduDuTrace(corrige, solution) {
    return {
        ...(corrige.methode !== undefined
            ? { methode: { outilsExclus: [...corrige.methode.outilsExclus] } }
            : {}),
        cibles: corrige.cibles,
        /* `erreur` n'est pas un argument de mesure : le vérificateur ne la lit pas. */
        contraintes: corrige.contraintes.map(({ erreur: _erreur, ...c }) => c),
        solution,
        etapesCles: [],
        explication: '',
        erreursVisees: [],
    };
}
/** Le nom d'un point de la figure d'énoncé : « A », « C », « M′ ». */
const nomDePointDynamique = z.string().trim().min(1).max(8);
/** Une mesure qui suit la figure (L2, F2) : une longueur, un angle, ou les coordonnées d'un point. */
const mesureDynamiqueSchema = z.discriminatedUnion('type', [
    z
        .object({ type: z.literal('longueur'), de: nomDePointDynamique, a: nomDePointDynamique })
        .strict(),
    z
        .object({
        type: z.literal('angle'),
        sommet: nomDePointDynamique,
        de: nomDePointDynamique,
        a: nomDePointDynamique,
    })
        .strict(),
    z.object({ type: z.literal('coordonnees'), point: nomDePointDynamique }).strict(),
]);
/**
 * LA FIGURE DYNAMIQUE d'un énoncé (atelier de figures, lot L2, F2). Une
 * construction EXPLICITE, sans solveur (décision 1 du fondateur, 17 octobre) :
 * déplacer un point redessine ce qui le nomme ; `sur` (« e:3 », le rang d'un
 * cercle, d'un segment ou d'une droite de la figure) le garde sur cet objet.
 */
export const figureDynamiqueSchema = z
    .object({
    points: z
        .array(z
        .object({
        nom: nomDePointDynamique,
        sur: z
            .string()
            .regex(/^e:\d+$/u, '« e:3 » : le rang d’un objet de la figure')
            .optional(),
    })
        .strict())
        .min(1)
        .max(4),
    mesures: z.array(mesureDynamiqueSchema).max(6).optional(),
    /** L'aimantation d'un glisser, en unités de figure. Défaut : un dixième de case. */
    pas: z.number().finite().positive().optional(),
})
    .strict();
/** Les instruments virtuels qu'une figure d'énoncé peut offrir (atelier de figures, L6, F12). */
export const INSTRUMENTS_DE_MESURE = ['rapporteur', 'regle'];
/**
 * LES INSTRUMENTS D'UNE FIGURE D'ÉNONCÉ (atelier de figures, lot L6, F12 ;
 * CONCEPTION § 2) : un rapporteur qu'on POSE sur le sommet, qu'on ORIENTE sur
 * un côté et sur lequel on LIT la mesure, comme sur papier (sec. 1) ; une règle
 * graduée dans l'unité de la figure. La réponse est la mesure LUE, écrite dans
 * le champ d'un `nombre` et corrigée par sa voie normale (sa `tolerance`).
 *
 * `mesure` : CE QUI EST À MESURER, facultatif — un angle (sommet, de, a) pour le
 * rapporteur, une longueur (de, a) pour la règle. Écrite, l'import vérifie que
 * la réponse attendue EST la mesure de la figure (`griefsInstruments`), et
 * l'élève peut voir, après la correction, l'instrument bien posé.
 *
 * ADDITIF : absent, la figure se lit comme avant.
 */
export const instrumentsFigureSchema = z
    .object({
    outils: z.array(z.enum(INSTRUMENTS_DE_MESURE)).min(1).max(2),
    mesure: z
        .discriminatedUnion('type', [
        z
            .object({
            type: z.literal('longueur'),
            de: nomDePointDynamique,
            a: nomDePointDynamique,
        })
            .strict(),
        z
            .object({
            type: z.literal('angle'),
            sommet: nomDePointDynamique,
            de: nomDePointDynamique,
            a: nomDePointDynamique,
        })
            .strict(),
    ])
        .optional(),
})
    .strict();
/**
 * LA FIGURE D'UN ÉNONCÉ (analyse 05, L-1) — « voici le graphique : lis le taux
 * de variation ». La même forme que l'illustration `figure_v2` d'une fiche
 * (`FigureV2Fiche`) : une figure v2, telle que la banque et l'atelier la
 * décrivent, et ce qu'elle montre pour qui ne la voit pas. En LECTURE SEULE :
 * l'élève la regarde, il n'y trace rien — la réponse par un tracé est une autre
 * nature de réponse, qui n'existe pas encore.
 *
 * Le contrat de la figure elle-même est tenu par `normaliserFigureV2`, à la
 * validation du dossier (`valider-exercices.ts`).
 */
export const figureEnonceSchema = z
    .object({
    genre: z.literal('figure_v2'),
    decrit: z.string().min(20).max(900),
    figure: z.record(z.unknown()),
    /**
     * Atelier de figures, L2 (F2) — ADDITIF : les points que l'élève DÉPLACE,
     * et les mesures qui se mettent à jour en direct. Rien n'est noté ; la
     * question qui suit se répond par écrit. La forme fine est tenue par
     * `griefsFigureDynamique`, à l'import.
     */
    dynamique: figureDynamiqueSchema.optional(),
    /**
     * Atelier de figures, L6 (F12) — ADDITIF : le rapporteur et la règle
     * graduée qu'on pose sur la figure pour LIRE une mesure. La forme fine est
     * tenue par `griefsInstruments`, à l'import.
     */
    instruments: instrumentsFigureSchema.optional(),
})
    .strict();
/**
 * UN TEXTE SUPPORT (analyse 06, C-5) : le récit, l'article, la fable sur lequel
 * l'exercice fait travailler. Il n'est PAS dans `texte` : l'écran le garde
 * lisible dans un bloc à part pendant que l'élève répond, et il ne part qu'une
 * fois au modèle. Les champs sont ceux de `SupportTravail`.
 */
export const texteSupportSchema = z
    .object({
    /** Le code du dossier : `T1`. */
    code: z.string().min(1),
    titre: z.string().trim().min(1),
    genre: z.string().trim().min(1).optional(),
    /** Pour un texte du domaine public : auteur, œuvre, année. */
    reference: z.string().trim().min(1).optional(),
    nbMots: z.number().int().min(1).optional(),
    paragraphes: z.array(z.string().trim().min(1)).min(1),
})
    .strict();
/** `ContentItem.enonce` d'un exercice du manuel. */
export const enonceExerciceSchema = z
    .object({
    /** Obligatoire au niveau 4. */
    titre: z.string().trim().min(1).optional(),
    texte: texteRiche,
    categorie: z.enum(CATEGORIES_EXERCICE),
    /** Pour un `choix` et un `vrai_faux` : ce que l'élève coche. */
    options: z
        .array(z
        .object({
        id: z.string().min(1),
        texte: texteRiche,
        /** `CAP-11` : l'option EST une figure — un solide, ou un développement à reconnaître. */
        solide: solideDecritSchema.optional(),
    })
        .strict())
        .min(2)
        .optional(),
    /** Facultative : la figure que l'énoncé donne à lire. */
    figure: figureEnonceSchema.optional(),
    /** Facultative : la table de valeurs que l'énoncé donne à lire (`CAP-09`). */
    table: tableDeValeursSchema.optional(),
    /** Facultatif : le diagramme statistique que l'énoncé donne à lire (`CAP-08`). */
    diagramme: diagrammeDecritSchema.optional(),
    /** Facultatif : le solide que l'énoncé donne à lire (`CAP-11`) — perspective, développement, ou les deux. */
    solide: solideDecritSchema.optional(),
    /**
     * Facultative : UNE EXPÉRIENCE ALÉATOIRE à simuler (atelier de figures, L5,
     * F11). Rien n'y est noté : la question se corrige par sa voie normale.
     */
    experience: experienceSchema.optional(),
    /**
     * Facultatif : LE BROUILLON DE FIGURE PERMIS (atelier de figures, L4, F5) —
     * en situation-problème et au niveau Raisonner. Jamais noté.
     */
    brouillonDeFigure: brouillonDeFigureSchema.optional(),
    /** Facultatifs : le ou les textes à lire avant de répondre, dans l'ordre. */
    supports: z.array(texteSupportSchema).min(1).optional(),
    /**
     * Facultative : UNE TÂCHE D'ÉCRITURE (`CAP-04`, P3-a) — genre, longueur
     * cible, contraintes vérifiables par le code. Ce que l'élève lit avant
     * d'écrire ; la grille, elle, est dans `ContentItem.grilleCorrection`.
     */
    ecriture: consigneEcritureSchema.optional(),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* 3. Le format d'échange — exercices/*.json et verification-exercices.json    */
/* -------------------------------------------------------------------------- */
/** Le sous-dossier des exercices : un fichier `.json` par notion, plus les situations-problèmes. */
export const DOSSIER_EXERCICES = 'exercices';
/** Le rapport de vérification par calcul, versé au dossier du chapitre (MV-CNT-04). */
export const FICHIER_VERIFICATION_EXERCICES = 'verification-exercices.json';
export const FAMILLES_IMPORT_EXERCICES = ['exercice'];
/** Le gabarit qu'un exercice importé porte en base — jamais le préfixe des défis. */
export const GABARIT_EXERCICE_MANUEL = 'manuel-vivant-v1';
/** La somme des poids d'un exercice, et sa tolérance (MV-EXO-01). */
export const TOLERANCE_POIDS_NOTION = 0.001;
/* ── Les fichiers compagnons d'une matière sans calcul (analyse 06, C-5, C-8) ── */
/** Le sous-dossier des fiches de correction : un fichier par fichier d'exercices. */
export const DOSSIER_CORRECTIONS = 'corrections';
/** Les textes supports du chapitre. */
export const FICHIER_TEXTES = 'textes/textes.json';
/** Qui corrige : le code seul (un choix), ou le modèle sur la grille. */
export const CORRECTEURS_EXERCICE = ['deterministe', 'modele'];
/* -------------------------------------------------------------------------- */
/* 4. Ce que l'élève lit et envoie — /v1/manuel                               */
/* -------------------------------------------------------------------------- */
/** Où en est l'élève sur UN exercice. */
export const ETATS_EXERCICE = ['jamais', 'essaye', 'reussi'];
/**
 * Les ateliers du menu « Exercices » : maths, « Atelier de tracés » ;
 * français, « Atelier d'écriture » (l'élève rédige au moins un paragraphe) et
 * « Atelier de lecture » (il répond à des questions sur un texte).
 */
export const ATELIERS_EXERCICE = ['traces', 'ecriture', 'lecture'];
export const LIBELLES_ATELIER = {
    traces: 'Atelier de tracés',
    ecriture: 'Atelier d’écriture',
    lecture: 'Atelier de lecture',
};
/** Qui a lu la démarche. Sert à l'écran pour dire, au besoin, qu'elle le sera plus tard. */
export const LECTURES_DEMARCHE = [
    /** Aucune démarche à lire : la correction est celle du calcul. */
    'sans_objet',
    /** Le modèle a lu la démarche. */
    'lue',
    /**
     * Il y avait une démarche à lire et le modèle n'a pas répondu : la correction
     * rendue est celle du calcul seul. L'écran dit « Ta démarche sera relue plus
     * tard. »
     */
    'differee',
];
/** Le libellé du repli quand la démarche n'a pas pu être lue. */
export const LIBELLE_DEMARCHE_DIFFEREE = 'Ta démarche sera relue plus tard.';
/** Les trois verdicts d'un exercice du manuel — un sous-ensemble de `VerdictCopie`. */
export const VERDICTS_EXERCICE_MANUEL = ['juste', 'partiel', 'erreur'];
/** Le titre du bandeau de verdict. Jamais « faux » : « à revoir ». */
export const TITRES_VERDICT_EXERCICE = {
    juste: 'C’est juste.',
    partiel: 'Presque.',
    erreur: 'À revoir ensemble.',
};
/** Le mot d'une ligne lue. */
export const MOTS_VERDICT_LIGNE = {
    juste: 'Juste',
    erreur: 'À revoir',
    partiel: 'Il manque une étape',
};
/** Les mots de l'écran de retour (rétroaction P0). */
export const TITRE_REUSSITES = 'Ce que tu as réussi';
export const TITRE_LA_CHOSE = 'La chose à retravailler';
export const TITRE_AUTRES_ERREURS = 'Aussi à revoir';
export const TITRE_EXEMPLE_ANNOTE = 'Un exemple du manuel';
export const TITRE_FIL_DE_LA_NOTION = 'Où tu en es';
/** Les deux mots d'un critère lu. Jamais « faux ». */
export const MOTS_CRITERE_LU = { tenu: 'Tenu', aRevoir: 'À revoir' };
/**
 * D'OÙ VIENT LA CORRECTION d'une réponse fermée (tranche T13b ; F-74, F-76, F-77) :
 *  - `prevue`  une réponse fautive écrite d'avance a été reconnue : son message
 *              est servi, sans appel de modèle ;
 *  - `calcul`  le calcul a suffi (réponse prouvée juste, forme à reprendre…),
 *              ou il l'a emporté sur le modèle ;
 *  - `modele`  le modèle a corrigé en direct : `retroaction` est SON explication,
 *              propre à la saisie, et `demarcheAlignee` l'accompagne ;
 *  - `degrade` le modèle aurait dû corriger et ne l'a pas pu (crédits épuisés,
 *              modèle indisponible, sortie hors contrat) : verdict du calcul,
 *              message générique, fiche de la notion.
 */
export const SOURCES_CORRECTION = ['prevue', 'calcul', 'modele', 'degrade'];
/** Les mots de l'écran pour la démarche alignée et le mode dégradé. */
export const TITRE_DEMARCHE_ALIGNEE = 'Ta réponse, pas à pas';
export const MESSAGE_CORRECTION_DEGRADEE = 'Ce n’est pas encore ça. L’explication détaillée n’est pas disponible pour le moment : la fiche ci-dessous reprend la méthode.';
/** Refus d'un indice généré qui n'a pas pu l'être (503 `INDICE_INDISPONIBLE`). */
export const CODE_INDICE_INDISPONIBLE = 'INDICE_INDISPONIBLE';
export const MESSAGE_INDICE_INDISPONIBLE = 'L’indice n’est pas disponible pour le moment. Relis la fiche de la notion, puis réessaie.';
/** Le barème de toute appréciation : toujours sur 5 (AP-1). */
export const BAREME_APPRECIATION = 5;
/** Pourquoi CE remède est servi. */
export const RAISONS_REMEDE = [
    /** Une erreur du catalogue est nommée et son piège est en service. */
    'erreur_nommee',
    /** Aucune erreur reconnue, ou erreur sans remède : la fiche de la notion principale. */
    'notion',
    /** Un calcul a glissé : la méthode est juste, la fiche n'est proposée qu'en second. */
    'calcul_glisse',
];
/** Le message du remède quand un calcul a glissé (MV-EXO-09). */
export const MESSAGE_CALCUL_GLISSE = 'Ta méthode est juste. Un calcul a glissé en route : refais-le.';
/** Les gestes proposés après une correction (MV-EXO-09). */
export const GESTES_SUITE = ['refaire', 'exercice_suivant', 'premier_du_niveau_suivant'];
/* -------------------------------------------------------------------------- */
/* 6. Relire un exercice — refonte de la console, L0 (15 octobre 2026)        */
/* -------------------------------------------------------------------------- */
/**
 * Les deux verdicts d'un relecteur sur un exercice du manuel (décision D-1 du
 * fondateur) : « relu, rien à changer », ou « à corriger », avec une note. Un
 * exercice ne s'ÉDITE PAS dans la console : la reprise se fait dans sa source,
 * puis par l'import.
 */
export const VERDICTS_RELECTURE_EXERCICE = ['relu', 'a_corriger'];
/** La note d'un « à corriger » : obligatoire, bornée. */
export const MAX_CARACTERES_NOTE_RELECTURE = 2000;
/* -------------------------------------------------------------------------- */
/* 7. Retirer un exercice du service — décision du fondateur, 15 octobre 2026 */
/* -------------------------------------------------------------------------- */
/** Le motif d'un retrait : obligatoire, borné. */
export const MAX_CARACTERES_MOTIF_RETRAIT = 1000;
//# sourceMappingURL=manuel-exercices.js.map